import {
  useWSModalCustom,
  WSInfoBox,
  WSLoader,
  WSModal,
  WSText
} from "@wingspanhq/fe-component-library";
import { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { VerificationLevel } from "@wingspanhq/wingspan-api-sdk";
import { queriesOnboarding } from "@query";
import { useQueryPrimaryComplianceEntity } from "../../../../shared/hooks/useQueryPrimaryComplianceEntity";
import { Flow } from "../../../../shared/components/Flow";
import { LocationStateAlert } from "../../../../shared/hooks/useLocationStateAlert";
import { FormDataAccountType } from "../../components/FormAccountType";
import { FormDataBusinessInformation } from "../../components/FormBusinessInformation";
import { FormDataPersonalInformation } from "../../components/FormPersonalInformation";
import { FormDataRepresentative } from "../../components/FormRepresentative";
import { useRequiresBankingVerification } from "../../hooks/useRequiresBankingVerification";
import { getSteps } from "./getSteps";
import { FormDataBusinessLegalName } from "./steps/StepImmaterialUpdateBusinessLegalName";
import { FormDataIndividualLegalName } from "./steps/StepImmaterialUpdateIndividualLegalName";
import { FormDataReason } from "./steps/StepReason";
import { PATH_ONBOARDING_CHANGE } from "../../index";
import { selectorIsEntityVerified } from "../../selectors/selectorIsEntityVerified";
import { FormDataUSInternationalPersonInformation } from "../../components/FormUSInternationalPersonInformation";
import { useIsQueriesFetched } from "../../../../shared/hooks/useIsQueriesFetched";

export type FlowMEChangeContext = {
  reason?: FormDataReason;
  accountType?: FormDataAccountType;
  personalInformation?: FormDataPersonalInformation;
  usInternationalPersonInformation?: FormDataUSInternationalPersonInformation;
  businessInformation?: FormDataBusinessInformation;
  representative?: FormDataRepresentative;
  individualLegalName?: FormDataIndividualLegalName;
  businessLegalName?: FormDataBusinessLegalName;
  nameChanged?: boolean;
  materialChangeCompleted?: boolean;
};

export const RouteChange: React.FC = () => {
  const queryMonitoredEntity = useQueryPrimaryComplianceEntity();
  const queryVerifications = queriesOnboarding.getOnboardingStatus.useQuery(
    undefined,
    { select: s => s.verifications }
  );
  const requiresBanking = useRequiresBankingVerification();
  const history = useHistory<LocationStateAlert>();
  const modalInfo = useWSModalCustom(ModalAbandon);

  const verificationKey = requiresBanking ? "banking" : "tax";

  const initialVerificationStatus = useMemo(
    () =>
      queryVerifications.data &&
      selectorIsEntityVerified(queryVerifications.data)
        ? VerificationLevel.VERIFIED
        : queryVerifications.data?.[verificationKey],
    // Check this only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const currentVerificationStatus = useMemo(
    () =>
      queryVerifications.data &&
      selectorIsEntityVerified(queryVerifications.data)
        ? VerificationLevel.VERIFIED
        : queryVerifications.data?.[verificationKey],
    [queryVerifications.data, verificationKey]
  );

  const goBack = useCallback(() => {
    if (queryMonitoredEntity.data?.type === "Business") {
      history.push("/member/settings/account/business");
    } else {
      history.push("/member/settings/account/personal");
    }
  }, [history, queryMonitoredEntity.data?.type]);

  const onNext = useCallback(() => {
    if (currentVerificationStatus === VerificationLevel.VERIFIED) {
      history.push("/member/dashboard", {
        alert: {
          theme: "success",
          icon: "check-circle",
          title: "Taxpayer information updated",
          children:
            "Your taxpayer information was successfully updated in the account settings."
        }
      });
      return;
    }

    history.push("/member/dashboard");
  }, [history, currentVerificationStatus]);

  const isFetched = useIsQueriesFetched(
    queryMonitoredEntity,
    queryVerifications
  );

  if (!isFetched) {
    return <WSLoader.Spinner mt="3XL" />;
  }

  return (
    <Flow
      basePath={PATH_ONBOARDING_CHANGE}
      steps={context =>
        getSteps(
          context,
          queryMonitoredEntity.data,
          initialVerificationStatus,
          currentVerificationStatus
        )
      }
      onBack={(context: FlowMEChangeContext) => {
        if (!!context.reason) {
          modalInfo.open({
            onContinue: goBack
          });
        } else {
          goBack();
        }
      }}
      onNext={onNext}
    />
  );
};

const ModalAbandon: React.FC<{
  onClose: () => void;
  onContinue: () => void;
}> = props => (
  <WSModal
    size="S"
    divider
    title="Exit update request"
    actions={{
      alignment: "fill",
      buttons: [
        {
          kind: "Secondary",
          label: "Cancel",
          onClick: props.onClose
        },
        {
          destructive: true,
          label: "Exit without changes",
          onClick: props.onContinue
        }
      ]
    }}
    onClose={props.onClose}
  >
    <WSText.ParagraphSm mb="L" color="gray600">
      Are you sure you want to exit update request for verified fields?
    </WSText.ParagraphSm>
    <WSInfoBox>Any unsaved changes not submitted will be lost.</WSInfoBox>
  </WSModal>
);
